import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, imgSrc } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import PageHeader from "@/components/PageHeader";
import { FileText, CalendarClock, CheckCircle2, Images, Plus, ArrowUpRight } from "lucide-react";

const STAT_META = [
  { key: "drafts", label: "Brouillons", icon: FileText },
  { key: "scheduled", label: "Planifiés", icon: CalendarClock },
  { key: "published", label: "Publiés", icon: CheckCircle2 },
  { key: "products", label: "Produits", icon: Images },
];

function statusBadge(status) {
  const map = {
    draft: { label: "Brouillon", cls: "text-muted-foreground border-white/20" },
    scheduled: { label: "Planifié", cls: "text-accent border-accent/40" },
    published: { label: "Publié", cls: "text-emerald-400 border-emerald-500/40" },
  };
  return map[status] || map.draft;
}

export default function Overview() {
  const { activeId, activeClient } = useClient();
  const [stats, setStats] = useState({ drafts: 0, scheduled: 0, published: 0, products: 0 });
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    if (!activeId) return;
    api.get("/stats", { params: { client_id: activeId } }).then(({ data }) => setStats(data)).catch(() => {});
    api.get("/posts", { params: { client_id: activeId } }).then(({ data }) => setPosts(data.slice(0, 6))).catch(() => {});
  }, [activeId]);

  return (
    <>
      <PageHeader overline={`Studio · ${activeClient?.name || "—"}`} title="Aperçu">
        <Link
          to="/composer"
          data-testid="new-post-cta"
          className="flex items-center gap-2 bg-primary text-primary-foreground rounded-sm px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-primary/80 hover:-translate-y-[1px] transition-all duration-150"
        >
          <Plus className="h-4 w-4" />
          Nouveau Post
        </Link>
      </PageHeader>

      <div className="p-6 lg:p-8 space-y-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 border-t border-l border-white/10">
          {STAT_META.map((s, i) => (
            <div
              key={s.key}
              data-testid={`stat-${s.key}`}
              className="border-r border-b border-white/10 p-6 bg-secondary animate-fade-up"
              style={{ animationDelay: `${i * 50}ms` }}
            >
              <s.icon className="h-5 w-5 text-muted-foreground mb-4" />
              <p className="font-heading text-4xl font-black tracking-tight">{stats[s.key]}</p>
              <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-muted-foreground mt-1">
                {s.label}
              </p>
            </div>
          ))}
        </div>

        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl font-bold tracking-tight">Posts récents</h2>
            <Link
              to="/calendrier"
              className="text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
            >
              Tout voir <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>

          {posts.length === 0 ? (
            <div className="border border-dashed border-white/15 rounded-sm p-12 text-center">
              <p className="text-sm text-muted-foreground mb-4">
                Aucun post pour cette marque. Crée ton premier post.
              </p>
              <Link
                to="/composer"
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-sm px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-primary/80 transition-colors"
              >
                <Plus className="h-4 w-4" /> Commencer
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {posts.map((p) => {
                const b = statusBadge(p.status);
                return (
                  <div
                    key={p.id}
                    data-testid={`recent-post-${p.id}`}
                    className="border border-white/10 rounded-sm bg-secondary flex overflow-hidden"
                  >
                    <div className="w-24 shrink-0 bg-[#0d0d0d] flex items-center justify-center">
                      <img src={imgSrc(p.image_url)} alt="" className="w-full h-full object-cover" />
                    </div>
                    <div className="p-3 flex-1 min-w-0">
                      <span className={`inline-block text-[9px] font-bold uppercase tracking-wider border rounded-sm px-1.5 py-0.5 mb-2 ${b.cls}`}>
                        {b.label}
                      </span>
                      <p className="text-xs leading-snug line-clamp-3 text-muted-foreground">
                        {p.caption || p.product_title}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
